// React and core hooks
import { useContext, useEffect, useState } from "react";

// React Router
import { useNavigate } from "react-router-dom";

// Third-party libraries
import _ from "lodash";
import { useTranslation } from "react-i18next";

// MUI components and icons
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Typography,
  DialogActions,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

// Project-specific imports
import { AuthenticationContext } from "@cloudmlm/cloudmlm-auth";
import urlConstant from "../../constants/urlConstants";
import dataConstant from "../../constants/dataConstants";
import { httpGet, httpPost } from "../../services/commonService";
import DataCardGrid from "../../shared-components/DataCardGrid/DataCardGrid";
import CommonSnackBar from "../../shared-components/snackbar/commonSnackbar";
import SnackState from "../../shared-components/snackbar/snackState";

const ProjectList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { accessToken, impersonateEmail } = useContext(AuthenticationContext);
  const [snack, closeSnack, showSnackbar] = SnackState();
  const [reload, setReload] = useState(false);
  const [selectedItem, setSelectedItem] = useState(false);
  const [isDeletePopupOpen, setDeletePopupOpen] = useState(false);
  const [isAddLeadPopupOpen, setAddLeadPopupOpen] = useState(false);
  const [projectList, setProjectList] = useState([]);

  const handleViewDetails = (data) => {
    navigate(`/manageProjects/${data.LeadCollectionID}`, {
      state: {
        IsMentor: data.IsMentor,
        businessType: data.BusinessType,
      },
    });
  };

  const handleOpenDeletePopup = (row) => {
    setSelectedItem(row);
    setDeletePopupOpen(true);
  };

  const handleCloseDeletePopup = () => {
    setDeletePopupOpen(false);
  };

  const handleCloseAddLeadPopup = () => {
    setAddLeadPopupOpen(false);
  };

  const deleteLead = () => {
    const modelData = {
      Ids: selectedItem.LeadCollectionID,
      LeadStatus: dataConstant.LeadCollections.LeadStatus.Deleted,
    };
    const deleteURL = urlConstant.LeadCollections.LeadCollectionDelete;
    httpPost(deleteURL, modelData, true, true, accessToken, impersonateEmail)
      .then((response) => {
        if (response.Status === dataConstant.APIResponseStatus.Success) {
          setDeletePopupOpen(false);
          showSnackbar(response.description, "success");
          setReload(!reload);
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  useEffect(() => {
    if (projectList.length === 0) {
      getBusinessTypes();
    }
  }, [projectList]);

  const columns = [
    {
      key: "LeadCollectionID",
      label: t("LeadCollection.LeadIdLable"),
      type: "text",
      isFilterVisible: true,
    },
    {
      key: "ProjectID",
      label: t("LeadCollection.ProjectId"),
      type: "text",
      isFilterVisible: true,
    },
    {
      key: "StatusUpdatedDateTimeWithTimeZone",
      label: t("LeadCollection.StatusUpdatedDate"),
      type: "date",
      isFilterVisible: true,
    },
    {
      key: "LeadStatus",
      label: t("LeadCollection.LeadStatus"),
      type: "text",
      isFilterVisible: true,
    },

    {
      key: "FirstName",
      label: t("LeadCollection.ListFirstNameLable"),
      type: "text",
      isFilterVisible: true,
    },
    {
      key: "LastName",
      label: t("LeadCollection.ListLastNameLable"),
      type: "text",
      isFilterVisible: true,
    },
    {
      key: "Email",
      label: t("LeadCollection.ListEmailLable"),
      type: "text",
      isFilterVisible: true,
    },
    {
      key: "PhoneNumber",
      label: t("LeadCollection.ListPhoneLable"),
      type: "text",
      isFilterVisible: true,
    },
    {
      key: "LeadFrom",
      label: t("LeadCollection.LeadFromLable"),
      type: "text",
    },
  ];

  const getBusinessTypes = async () => {
    await httpGet(
      urlConstant.Common.GetBusinessTypeDropDownList,
      true,
      false,
      accessToken,
      impersonateEmail
    )
      .then((response) => {
        setProjectList(response.Value.Data);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const fetchDataFunction = async ({
    page,
    pageSize,
    search,
    filters,
    sortKey,
    sortDirection,
  }) => {
    const pagingInfo = {
      PageNumber: page,
      RowsPerPage: pageSize,
      CommonSearchText: search,
      SortColumns: {
        SortColumnName: sortKey,
        SortOrder: sortDirection,
      },
      Filter: {
        LeadCollectionIDFilter: filters?.LeadCollectionID,
        FirstNameFilter: filters?.FirstName,
        LastNameFilter: filters?.LastName,
        EmailFilter: filters?.Email,
        PhoneNumberFilter: filters?.PhoneNumber,
        LeadStatusFilter: filters?.LeadStatus,
        LeadProjectIDFilter: filters?.ProjectID,
        LeadProjectStatusFilter: filters?.ProjectStatus,
        StatusUpdatedDateTimeFilter: filters?.StatusUpdatedDateTimeWithTimeZone,
      },
    };
    const requestBody = {
      PagingInfoVM: pagingInfo,
    };
    try {
      const response = await httpPost(
        urlConstant.LeadCollections.GetLeadCollectionListByUserId,
        requestBody,
        true,
        false,
        accessToken,
        impersonateEmail
      );
      if (response?.Value?.Status === dataConstant.APIResponseStatus.Success) {
        const leadList = _.map(response.Value.Data.Data, (item) => ({
          ...item,
          isPencil: true,
          isDelete: !item.IsMentor && item.ProjectStatus !== "Deleted",
        }));

        return {
          data: leadList,
          totalCount: response.Value.Data.TotalCount,
        };
      } else {
        return { data: [], totalCount: 0 };
      }
    } catch (err) {
      console.error(err);
    }
  };

  const gridButtons = [
    {
      id: "view-details",
      label: "Edit",
      icon: "pencil",
      color: "secondary-main",
      onClick: handleViewDetails,
    },
    {
      id: "delete",
      label: "Delete",
      icon: "trash",
      color: "secondary-main",
      onClick: handleOpenDeletePopup,
    },
  ];

  return (
    <>
      <Box flexDirection={"column"} gap={2} display={"flex"} width={"100%"}>
        <Typography
          color="secondary.light"
          display={{ xs: "flex", sm: "none" }}
          variant="h4"
          fontSize={"24px"}
          fontWeight={600}
        >
          {t("LeadCollection.Leads")}
        </Typography>
        <Card
          sx={(theme) => ({
            p: {
              xs: "20px",
              sm: "25px 30px",
            },
            background: theme.palette.background.cardGradient,
            border: "1px solid var(--headerbg)",
            borderRadius: "20px",
            height: "100%",
          })}
        >
          <Stack gap={"20px"}>
            <Stack
              direction={"row"}
              gap={1}
              width={"100%"}
              alignItems={"center"}
              justifyContent={"space-between"}
              flexWrap={"wrap"}
            >
              <Typography color="secondary.light" variant="h4" fontWeight={600}>
                {t("LeadCollection.LeadList")}
              </Typography>
              <Button
                variant="contained"
                sx={{ px: 3, width: { xs: "100%", sm: "auto" } }}
                onClick={() => setAddLeadPopupOpen(true)}
              >
                {t("LeadCollection.AddLead")}
              </Button>
            </Stack>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 12, lg: 12 }}>
                <DataCardGrid
                  key={reload}
                  onFetchData={fetchDataFunction}
                  columns={columns}
                  defaultItemsPerPage={8}
                  defaultColumnsPerGrid={3}
                  enableExport={true}
                  enablePagination={true}
                  uniqueId="projects-grid"
                  actionButtons={gridButtons}
                />
              </Grid>
            </Grid>
          </Stack>
        </Card>
      </Box>

      {/* Add Lead Popup - Choose Business Type */}
      <Dialog
        open={isAddLeadPopupOpen}
        aria-labelledby="customized-dialog-title"
        sx={{ borderRadius: "20px", background: "transparent" }}
      >
        <Stack
          sx={(theme) => ({
            background: theme.palette.common.black,
            borderTopLeftRadius: "20px",
            borderTopRightRadius: "20px",
          })}
        >
          <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
            {t("LeadCollection.AddLead")}
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleCloseAddLeadPopup}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
              p: 1,
              borderRadius: "8px",
            }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
        <DialogContent
          sx={(theme) => ({
            background: theme.palette.background.cardGradient,
            border: 0,
            borderBottomLeftRadius: "20px",
            borderBottomRightRadius: "20px",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            alignItems: "center",
          })}
          dividers
        >
          <Button
            variant="contained"
            sx={{ px: 0.8, py: 0.5, width: "auto", backgroundColor: "#FF6C6C" }}
            color="primary"
            disableElevation
          >
            <img src="/icons/user-header.svg" alt="user" />
          </Button>
          <Stack direction={"column"} alignItems={"center"}>
            <Typography color="secondary.light" variant="body1">
              {t("LeadCollection.LeadFor")}
            </Typography>
          </Stack>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            flexWrap={"wrap"}
            gap={2}
            width={"100%"}
            justifyContent={"center"}
            borderTop={"1px solid var(--headerbg)"}
            paddingTop={"20px"}
          >
            {projectList
            .filter(item => !item.isDisabled)
            .map((item, index) => {
              return (
                <Button
                  variant="contained"
                  sx={{ px: 3, width: { xs: "100%", sm: "auto" } }}
                  key={index}
                  onClick={() =>
                    navigate("/manageProjects", {
                      state: { businessType: item.label },
                    })
                  }
                >
                  {item.label}
                </Button>
              );
            })}
          </Stack>
        </DialogContent>
      </Dialog>

      {/* Delete Popup */}
      <Dialog
        open={isDeletePopupOpen}
        onClose={handleCloseDeletePopup}
        sx={{ borderRadius: "20px", background: "transparent" }}
      >
        <Stack
          sx={(theme) => ({
            background: theme.palette.common.black,
            borderTopLeftRadius: "20px",
            borderTopRightRadius: "20px",
          })}
        >
          <DialogTitle sx={{ fontWeight: "bold", px: 2 }}>
            {t("LeadCollection.Delete")}
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleCloseDeletePopup}
            sx={(theme) => ({
              position: "absolute",
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
              p: 1,
              borderRadius: "8px",
            })}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
        <DialogContent
          sx={(theme) => ({
            background: theme.palette.background.cardGradient,
            border: 0,
            borderBottomLeftRadius: "20px",
            borderBottomRightRadius: "20px",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            alignItems: "center",
          })}
          dividers
        >
          <Stack textAlign={"center"}>
            <Typography
              color="common.light"
              sx={{}}
              variant="h4"
              fontWeight={600}
            >
              <Typography variant="body1" sx={{ textAlign: "left" }}>
                {t("Are you sure you want to delete?")}
              </Typography>{" "}
            </Typography>
          </Stack>
          <DialogActions sx={{ flexDirection: "row", gap: 1, px: 2, pb: 2 }}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              sx={{ px: 3 }}
              onClick={handleCloseDeletePopup}
            >
              {t("Common.No")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={deleteLead}
              sx={{ px: 3 }}
            >
              {t("Common.Yes")}
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
      <CommonSnackBar snackObj={snack} closeSnack={closeSnack} buttonProp />
    </>
  );
};

export default ProjectList;